import config from '../config';
import { expireSession } from './authSession';
import { LEGAL_VERSION } from '../legal/2026-10-29-v2';

export const request = async (baseUrl, path, options = {}) => {
  const { skipAuth = false, headers: extraHeaders, ...fetchOptions } = options;
  const token = skipAuth ? null : localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extraHeaders || {})
  };

  const response = await fetch(`${baseUrl}${path}`, {
    ...fetchOptions,
    headers
  });

  if (!response.ok) {
    if (response.status === 401 && !skipAuth && (!extraHeaders?.Authorization || headers.Authorization === `Bearer ${token}`)) {
      expireSession(token);
    }
    const body = await response.json().catch(() => ({}));
    const detail = Array.isArray(body.detail) ? body.detail.map((item) => item.msg).join('; ') : body.detail;
    const error = new Error(detail || body.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return response.status === 204 ? null : response.json();
};

export const authApi = {
  signup: (email, password, acceptedTerms) => request(config.api.auth, '/auth/signup', {
    method: 'POST', skipAuth: true, body: JSON.stringify({ email, password, accepted_terms: acceptedTerms, legal_version: LEGAL_VERSION })
  }),
  confirmSignup: (email, code) => request(config.api.auth, '/auth/signup/confirm', {
    method: 'POST', skipAuth: true, body: JSON.stringify({ email, code })
  }),
  resendSignup: (email) => request(config.api.auth, '/auth/signup/resend', {
    method: 'POST', skipAuth: true, body: JSON.stringify({ email })
  }),
  login: (username, password) => request(config.api.auth, '/auth/login', {
    method: 'POST',
    skipAuth: true,
    body: JSON.stringify({ username, password })
  }),
  completePassword: (username, session, newPassword) => request(
    config.api.auth,
    '/auth/password/complete',
    {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        username,
        session,
        new_password: newPassword
      })
    }
  ),
  forgotPassword: (username) => request(config.api.auth, '/auth/forgot-password', {
    method: 'POST',
    skipAuth: true,
    body: JSON.stringify({ username })
  }),
  confirmForgotPassword: (username, code, newPassword) => request(
    config.api.auth,
    '/auth/confirm-forgot-password',
    {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ username, code, new_password: newPassword })
    }
  ),
  me: () => request(config.api.auth, '/auth/me'),
  meWithToken: (token) => request(config.api.auth, '/auth/me', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  contactSupport: (submission) => request(config.api.auth, '/auth/support', {
    method: 'POST',
    body: JSON.stringify(submission)
  }),
  preferences: () => request(config.api.auth, '/auth/preferences'),
  savePreferences: (preferences) => request(config.api.auth, '/auth/preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences)
  }),
  refresh: () => request(config.api.auth, '/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: localStorage.getItem('refresh_token') })
  })
};

export const adminApi = {
  users: () => request(config.api.auth, '/admin/users'),
  invite: (email, plan, isClient = false) => request(config.api.auth, '/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, plan, is_client: isClient || plan === 'client' })
  }),
  pause: (username) => request(config.api.auth, `/admin/users/${encodeURIComponent(username)}/pause`, { method: 'POST' }),
  resume: (username) => request(config.api.auth, `/admin/users/${encodeURIComponent(username)}/resume`, { method: 'POST' }),
  setPlan: (username, plan) => request(config.api.auth, `/admin/users/${encodeURIComponent(username)}/plan`, {
    method: 'PUT',
    body: JSON.stringify({ plan })
  }),
  setClientStatus: (username, isClient) => request(config.api.auth, `/admin/users/${encodeURIComponent(username)}/client`, {
    method: 'PUT',
    body: JSON.stringify({ is_client: isClient })
  }),
  sendPasswordReset: (username) => request(config.api.auth, `/admin/users/${encodeURIComponent(username)}/password-reset`, { method: 'POST' }),
  companies: () => request(config.api.auth, '/admin/companies'),
  costs: () => request(config.api.auth, '/admin/costs'),
  reportViews: ({ days, tz, plans } = {}) => request(
    config.api.auth,
    `/admin/report-views${reportQuery({ days, tz, plans: plans?.join(',') })}`
  ),
  researchConfig: () => request(config.api.auth, '/admin/config'),
  saveResearchConfig: (settings) => request(config.api.auth, '/admin/config', {
    method: 'PUT', body: JSON.stringify(settings)
  }),
  visibility: () => request(config.api.auth, '/admin/visibility'),
  saveVisibility: (matrix) => request(config.api.auth, '/admin/visibility', {
    method: 'PUT', body: JSON.stringify(matrix)
  }),
  plans: () => request(config.api.auth, '/admin/plans'),
  savePlans: (catalog) => request(config.api.auth, '/admin/plans', {
    method: 'PUT', body: JSON.stringify(catalog)
  }),
  clientReports: () => request(config.api.auth, '/admin/client-reports'),
  addClientReport: (ticker, companyName) => request(config.api.auth, '/admin/client-reports', {
    method: 'POST', body: JSON.stringify({ ticker, company_name: companyName })
  }),
  removeClientReport: (ticker) => request(config.api.auth, `/admin/client-reports/${encodeURIComponent(ticker)}`, { method: 'DELETE' }),
  tamaracSync: () => request(config.api.tamarac, '/tamarac/focused-stocks'),
  startTamaracSync: () => request(config.api.tamarac, '/tamarac/focused-stocks/sync', { method: 'POST' }),
  setCompanyExpiration: (tenantId, ticker, expiresAt) => request(
    config.api.auth,
    `/admin/companies/${encodeURIComponent(tenantId)}/${encodeURIComponent(ticker)}/expiration`,
    { method: 'PUT', body: JSON.stringify({ expires_at: expiresAt }) }
  ),
  removeCompany: (tenantId, ticker) => request(
    config.api.auth,
    `/admin/companies/${encodeURIComponent(tenantId)}/${encodeURIComponent(ticker)}`,
    { method: 'DELETE' }
  ),
  coverage: (status) => request(config.api.roster, `/admin/coverage${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  cancelCoverage: (id) => request(config.api.roster, `/admin/coverage/${encodeURIComponent(id)}/cancel`, { method: 'POST' })
};

export const researchApi = {
  companies: () => request(config.api.research, '/companies'),
  reconcile: () => request(config.api.research, '/companies/reconcile', { method: 'POST' }),
  unlock: (ticker) => request(config.api.research, `/companies/${encodeURIComponent(ticker)}/unlock`, { method: 'POST' }),
  setFullReport: (ticker, enabled) => request(config.api.research, `/companies/${encodeURIComponent(ticker)}/full-report`, {
    method: 'PUT', body: JSON.stringify({ enabled })
  }),
  addCompany: (ticker, companyName) => request(config.api.research, '/companies', {
    method: 'POST',
    body: JSON.stringify({ ticker, company_name: companyName })
  }),
  addFirstLookPick: (ticker, companyName) => request(config.api.research, '/firstlook-picks', {
    method: 'POST',
    body: JSON.stringify({ ticker, company_name: companyName })
  })
};

export const coverageApi = {
  request: (ticker, note = '') => request(config.api.roster, '/coverage/requests', {
    method: 'POST',
    body: JSON.stringify({ ticker, note })
  }),
  mine: () => request(config.api.roster, '/coverage/requests'),
  get: (id) => request(config.api.roster, `/coverage/requests/${encodeURIComponent(id)}`)
};

export const billingApi = {
  me: () => request(config.api.billing, '/billing/me'),
  changePlan: (planId) => request(config.api.billing, '/billing/change-plan', {
    method: 'POST', body: JSON.stringify({ plan_id: planId })
  }),
  sync: () => request(config.api.billing, '/billing/sync', { method: 'POST' }),
  deleteAccount: (submission) => request(config.api.billing, '/billing/account/delete', {
    method: 'POST', body: JSON.stringify(submission)
  }),
  prices: () => request(config.api.billing, '/billing/prices', { skipAuth: true }),
  checkoutOptions: () => request(config.api.billing, '/billing/checkout-options'),
  createCheckoutSession: (planId, interval) => request(
    config.api.billing,
    '/billing/create-checkout-session',
    { method: 'POST', body: JSON.stringify({ plan_id: planId, interval }) }
  )
};

const reportQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value != null && (value !== '' || key === 'plans')) search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

export const reportApi = {
  companyDirectory: () => request(config.api.reports, '/public/company-directory', { skipAuth: true }),
  hubs: () => request(config.api.reports, '/reports/hubs'),
  hub: (hub, params = {}) => request(
    config.api.reports,
    `/reports/hubs/${encodeURIComponent(hub)}${reportQuery({ version: params.version, group: params.group, ticker: params.ticker })}`
  ),
  hubVersions: (hub) => request(config.api.reports, `/reports/hubs/${encodeURIComponent(hub)}/versions`),
  publicHub: (hub, params = {}) => request(
    config.api.reports,
    `/public/hubs/${encodeURIComponent(hub)}${reportQuery({ version: params.version, group: params.group, ticker: params.ticker })}`,
    { skipAuth: true }
  ),
  publicPlans: () => request(config.api.reports, '/public/plans', { skipAuth: true }),
  preview: () => request(config.api.reports, '/public/preview', { skipAuth: true }),
  plans: () => request(config.api.reports, '/reports/plans'),
  company: (ticker, options = {}) => {
    const params = typeof options === 'string' ? { version: options } : options;
    return request(
      config.api.reports,
      `/reports/company/${encodeURIComponent(ticker)}${reportQuery({ version: params.version, product: params.product })}`
    );
  },
  versions: (ticker, product) => request(config.api.reports, `/reports/company/${encodeURIComponent(ticker)}/versions${reportQuery({ product })}`),
  companyMetadata: (tickers) => request(config.api.reports, '/reports/company-metadata', {
    method: 'POST', body: JSON.stringify({ tickers })
  }),
  recordView: (view) => request(config.api.reports, '/reports/views', {
    method: 'POST', body: JSON.stringify(view)
  }),
  // keepalive lets the final dwell time survive the tab closing.
  endView: (ref, seconds) => request(config.api.reports, '/reports/views/end', {
    method: 'POST', body: JSON.stringify({ ref, seconds }), keepalive: true
  }),
  publicStub: (ticker, product) => request(config.api.reports, `/public/stub/${encodeURIComponent(ticker)}${reportQuery({ product })}`, { skipAuth: true }),
  grammar: (version) => request(config.api.reports, `/reports/learn/grammar${reportQuery({ version })}`)
};

export const rosterApi = {
  adminCompanies: () => request(config.api.roster, '/roster/admin/companies'),
  setCompanyProfile: (ticker, profile_level) => request(
    config.api.roster, `/roster/admin/companies/${encodeURIComponent(ticker)}/profile`,
    { method: 'PUT', body: JSON.stringify({ profile_level }) }
  ),
  setCompanyPharmaLandscape: (ticker, enabled) => request(
    config.api.roster,
    `/roster/admin/companies/${encodeURIComponent(ticker)}/pharma-landscape`,
    { method: 'PUT', body: JSON.stringify({ enabled }) }
  ),
  deleteCompany: (ticker) => request(config.api.roster, `/roster/admin/companies/${encodeURIComponent(ticker)}`, {
    method: 'DELETE', body: JSON.stringify({ confirm_ticker: ticker })
  }),
  setCompanyLive: (ticker, live) => request(
    config.api.roster,
    `/roster/admin/companies/${encodeURIComponent(ticker)}/status`,
    { method: 'PUT', body: JSON.stringify({ live }) }
  ),
  buildCompany: (ticker, options = {}) => request(
    config.api.roster,
    `/roster/${encodeURIComponent(ticker)}/build`,
    { method: 'POST', body: JSON.stringify(options) }
  ),
  pulseRefreshCapability: () => request(config.api.roster, '/roster/pulse/refresh'),
  refreshPulse: () => request(config.api.roster, '/roster/pulse/refresh', { method: 'POST', body: '{}' }),
  pulseRefreshStatus: (execution) => request(config.api.roster, `/roster/pulse/refresh/${encodeURIComponent(execution)}`),
  buildHubs: (hubs, taxonomy = false) => request(config.api.roster, '/roster/hubs/build', {
    method: 'POST',
    body: JSON.stringify({ hubs, taxonomy })
  })
};
